import React, { useCallback, useEffect, useRef, useState } from 'react';
import SchoolHeader from '@/components/SchoolHeader';
import SummaryBox from '@/components/SummaryBox';
import CtaButton from '@/components/CtaButton';
import ErrorBanner from '@/components/ErrorBanner';
import PaymentMethodSection from '@/payment/PaymentMethodSection';
import { toggleAutoPay } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { getStripe } from '@/services/stripe';
import { amountBreakdownRows, formatDate } from '@/format';
import session from '@/session';

/**
 * Web·2 — enrollment. Card entry stays inside Stripe (PaymentElement);
 * the mandate checkbox and the enable action are ours. Also owns the
 * post-3DS-redirect resumption (resumeClientSecret).
 */
const SetUpScreen = ({
	school,
	status,
	assignment,
	studentName,
	onEnabled,
	onPendingVerification,
	onPayInvoiceInstead,
	resumeClientSecret,
	onResumeConsumed,
}) => {
	const [mandate, setMandate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [forceExpanded, setForceExpanded] = useState(false);
	// Filled by PaymentMethodSection once it knows what can be picked.
	const [selection, setSelection] = useState(null);
	const confirmRef = useRef(null);
	const handleBridge = useCallback((fn) => {
		confirmRef.current = fn;
	}, []);

	const studentId = status.student_id;
	const schoolName = school?.name ?? 'your school';
	const firstChargeAt = assignment.next_charge_at ?? assignment.first_charge_at;

	// One request: the server promotes `payment_method_id` to the payer's
	// default BEFORE it flips any plan, so the page cannot end up enrolled
	// on a method the server was never told about.
	const finishEnable = useCallback(
		async (forStudentId, paymentMethodId) => {
			await toggleAutoPay({
				student_id: forStudentId,
				enabled: true,
				mandate_accepted: true,
				payment_method_id: paymentMethodId,
			});
			session.clearPendingSetup();
			onEnabled();
		},
		[onEnabled]
	);

	// ACH microdeposits: the bank verifies over 1–2 business days. Store the
	// mandate as a PENDING enrollment (the setup_intent.succeeded webhook
	// completes it) and show the wait screen — no default-PM set, no flips.
	const registerPending = useCallback(
		async (setupIntent) => {
			await toggleAutoPay({
				student_id: studentId,
				enabled: true,
				mandate_accepted: true,
				pending_setup_intent_id: setupIntent.id,
			});
			session.clearPendingSetup();
			onPendingVerification(setupIntent.next_action?.verify_with_microdeposits ?? null);
		},
		[onPendingVerification, studentId]
	);

	// Post-3DS return: the bank redirect landed back here with the intent's
	// client secret in the query. Pick the sequence back up where it left off.
	useEffect(() => {
		if (!resumeClientSecret || !status.stripe_account_id) return;

		const resume = async () => {
			setBusy(true);
			try {
				const stripe = await getStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, status.stripe_account_id);
				const { setupIntent } = await stripe.retrieveSetupIntent(resumeClientSecret);
				const pending = session.getPendingSetup();

				if (setupIntent?.status === 'succeeded' && pending?.mandate) {
					await finishEnable(pending.student_id ?? studentId, setupIntent.payment_method);
					return;
				}
				if (
					setupIntent?.status === 'requires_action' &&
					setupIntent.next_action?.type === 'verify_with_microdeposits' &&
					pending?.mandate
				) {
					await registerPending(setupIntent);
					return;
				}
				setError('Your payment method wasn’t fully set up — please try again.');
			} catch (err) {
				setError(parseHostedError(err).message);
			} finally {
				setBusy(false);
				onResumeConsumed();
			}
		};

		resume();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resumeClientSecret, status.stripe_account_id]);

	const handleEnable = async () => {
		setError(null);
		setBusy(true);
		try {
			if (!selection || (selection.type === 'new' && !confirmRef.current)) {
				setForceExpanded(true);
				setError('Please add a payment method first.');
				return;
			}
			let paymentMethodId = selection.pmId;
			if (selection.type === 'new') {
				session.savePendingSetup({
					student_id: studentId,
					mandate: true,
				});
				const returnUrl = `${window.location.origin}/auto-pay${window.location.search}`;
				const { setupIntent, error: stripeError } = await confirmRef.current(returnUrl);
				if (stripeError) {
					setError(stripeError.message);
					return;
				}
				if (
					setupIntent?.status === 'requires_action' &&
					setupIntent.next_action?.type === 'verify_with_microdeposits'
				) {
					await registerPending(setupIntent);
					return;
				}
				paymentMethodId = setupIntent.payment_method;
			}

			await finishEnable(studentId, paymentMethodId);
		} catch (err) {
			const parsed = parseHostedError(err);
			if (parsed.code === 'payment_method_required') setForceExpanded(true);
			setError(parsed.message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-[18px]">
			<SchoolHeader name={school?.name} logo={school?.logo} />

			<h1 className="text-[24px] font-extrabold text-pay-ink">Set up Auto-Pay</h1>

			<p className="text-[14px] leading-[21px] text-pay-muted">
				Auto-Pay keeps {studentName ? `${studentName}’s` : 'your student’s'} tuition on time — we’ll charge your
				selected method automatically when each invoice is due, for every plan including ones added later.
			</p>

			<SummaryBox
				rows={[
					{ label: 'Student', value: studentName ?? '—' },
					...amountBreakdownRows(assignment, selection?.pmType),
					{ label: 'First charge', value: firstChargeAt ? formatDate(firstChargeAt) : '—' },
				]}
			/>

			<PaymentMethodSection
				studentId={studentId}
				stripeAccountId={status.stripe_account_id}
				defaultPm={status.default_payment_method}
				onSelectionChange={setSelection}
				onBridge={handleBridge}
				forceExpanded={forceExpanded}
			/>

			<label className="flex w-full cursor-pointer items-start gap-[10px] pt-[2px]">
				<input
					type="checkbox"
					checked={mandate}
					onChange={(event) => setMandate(event.target.checked)}
					className="mt-[2px] h-[20px] w-[20px] shrink-0 accent-pd-blue-new"
				/>
				<span className="text-[12.5px] leading-[18px] text-pay-muted">
					I authorize {schoolName} to automatically charge this payment method for each of{' '}
					{studentName ? `${studentName}’s` : 'my student’s'} tuition invoices — including plans added later — until I
					turn off Auto-Pay.
				</span>
			</label>

			<ErrorBanner>{error}</ErrorBanner>

			<CtaButton onClick={handleEnable} disabled={busy || !mandate || !selection}>
				{busy ? 'Turning on…' : 'Turn on Auto-Pay'}
			</CtaButton>

			{(status.open_invoice_count ?? 0) > 0 ? (
				<button
					type="button"
					onClick={onPayInvoiceInstead}
					className="w-full text-center text-[13px] font-semibold text-pd-blue-new"
				>
					Pay a single invoice instead →
				</button>
			) : null}
		</div>
	);
};

export default SetUpScreen;
